import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const COMPANY_LOGO = process.env.REACT_APP_COMPANY_LOGO;
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || "Vision AcryGlas";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(response.data.token, response.data.user);
      toast.success("Welcome back!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F9FA]" data-testid="login-page">
      {/* Logo Header */}
      <div className="bg-white py-6 px-4 flex flex-col items-center border-b border-[#E5E7EB]">
        <img 
          src={COMPANY_LOGO} 
          alt={COMPANY_NAME} 
          className="h-20 object-contain mb-2"
          data-testid="company-logo"
        />
        <p className="text-sm text-[#4B5563]">Sales CRM</p>
      </div>
      
      {/* Login Form */}
      <div className="flex-1 bg-white px-6 pt-8 pb-12 mt-4 mx-4 rounded-xl border border-[#E5E7EB]">
        <div className="max-w-md mx-auto">
          <h1 className="text-3xl font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Welcome Back
          </h1>
          <p className="text-[#4B5563] mb-8">Sign in to your {COMPANY_NAME} CRM</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5 h-12 border-[#E5E7EB] focus:border-[#0055FF] focus:ring-[#0055FF]/10"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0A0A0A] font-medium">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-12 pr-12 border-[#E5E7EB] focus:border-[#0055FF] focus:ring-[#0055FF]/10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full mt-6"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <p className="text-center mt-8 text-[#4B5563]">
            Don't have an account?{" "}
            <Link 
              to="/register" 
              className="text-[#0055FF] font-semibold hover:underline"
              data-testid="register-link"
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
